// The page reader's reading of a split's pages — what it read once per page,
// beside the splitter, with no question context, and which block that offers
// each question. Shown on the Solution Splitter run detail.
// See docs/FeatureDesigns/solution-analysis/solution-page-reader-target.md §10.
import { Badge } from '@shared-ui/components';
import { LatexText } from '@shared-ui/latex';
import type { PageReadingBlock, PageReadingMissReason, PageReadingView } from './solutionSplitterTypes';

/** What each reason means, in the words a reader of this page needs. */
const MISS_LABEL: Record<PageReadingMissReason, string> = {
  gate_off: 'gate off',
  corrected: 'region corrected',
  no_upload: 'no page for it',
  not_read: 'page not read',
  no_single_page: 'work spans pages',
  page_replaced: 'page edited since',
  read_failed: 'page read failed',
  no_block: 'no block with its number',
  duplicate_number: 'its number appears twice',
  not_readable: 'block not readable',
  continuation_missing: 'next page starts other work',
  continuation_ambiguous: 'reading says the work ends',
};

function QualityBadge({ quality }: { quality: string }) {
  const variant = quality === 'readable' ? 'success'
    : quality === 'blank' || quality === 'irrelevant' ? 'default'
      : 'warning';
  return <Badge variant={variant} size="sm">{quality}</Badge>;
}

function BlockRow({ block, servesQuestions }: { block: PageReadingBlock; servesQuestions: Array<{ questionIndex: number; continued: boolean }> }) {
  const segments = block.reading.extracted_segments ?? [];
  return (
    <div style={{ padding: '6px 0', borderTop: '1px solid var(--border-color, #2a2a2a)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
        {block.written_number !== null ? (
          <span className="tag" title={`Read from "${block.number_evidence}"`}>wrote &quot;{block.written_number}&quot;</span>
        ) : (
          <Badge variant="default" size="sm" title="No question number written for this block — it is never given to a question">no number</Badge>
        )}
        <QualityBadge quality={block.reading.work_quality} />
        {block.reading.marked_option && (
          <span className="tag" title={`Read from "${block.reading.marked_option.evidence}"`}>
            marked ({block.reading.marked_option.label})
          </span>
        )}
        {servesQuestions.length > 0 && (
          <Badge variant="success" size="sm" title="The question this block is offered to; 'cont.' is the continuation of work begun on an earlier page">
            → {servesQuestions.map(s => `Q${s.questionIndex + 1}${s.continued ? ' cont.' : ''}`).join(', ')}
          </Badge>
        )}
      </div>
      {segments.length > 0 ? (
        <div style={{ fontSize: 13, paddingLeft: 4 }}>
          {segments.map((seg, i) => (
            <div
              key={seg.segment_id ?? i}
              style={{
                textDecoration: seg.crossed_out ? 'line-through' : undefined,
                color: seg.crossed_out ? 'var(--text-secondary)' : undefined,
              }}
              title={[seg.segment_type, seg.semantic_description, seg.confidence !== undefined ? `confidence ${Math.round(seg.confidence * 100)}%` : '']
                .filter(Boolean).join(' · ')}
            >
              <LatexText text={seg.literal_text || seg.semantic_description || ''} />
            </div>
          ))}
        </div>
      ) : (
        <div className="muted" style={{ fontSize: 13 }}>No segments.</div>
      )}
    </div>
  );
}

export function SolutionPageReading({ reading }: { reading: PageReadingView }) {
  const served = reading.joins.filter(j => j.blockIndex !== undefined);
  const missed = reading.joins.filter(j => j.missing);
  // "page:block" → the questions it is offered to (every part, for work across pages).
  const servedBy = new Map<string, Array<{ questionIndex: number; continued: boolean }>>();
  for (const j of served) {
    const parts = j.parts ?? [{ pageIndex: j.pageIndex!, blockIndex: j.blockIndex! }];
    parts.forEach((p, k) => {
      const key = `${p.pageIndex}:${p.blockIndex}`;
      servedBy.set(key, [...(servedBy.get(key) ?? []), { questionIndex: j.questionIndex, continued: k > 0 }]);
    });
  }
  const missCounts = new Map<PageReadingMissReason, number>();
  for (const j of missed) missCounts.set(j.missing!, (missCounts.get(j.missing!) ?? 0) + 1);

  return (
    <div className="card">
      <h3>Page reading</h3>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Each page that can serve a question read once, after the split, with no question context. A question is offered the
        block on its page that carries its number; the analysis may still read it for itself if the page
        was edited, the mapping changed, its region was corrected, or the gate is off.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <Badge variant={reading.pagesFailed ? 'warning' : 'default'} size="sm">
          {reading.pagesRead} of {reading.pagesRead + reading.pagesFailed} pages read
        </Badge>
        {reading.multiPage && (
          <span className="tag" title="solution_page_reader_multi_page was on: pages of work across pages were read and joined">across pages</span>
        )}
        {!!reading.pagesNotNeeded && (
          <span className="tag" title="Pages with no single-page question and no answer list on them — long answers that run across pages — so a reading could serve nothing there">
            {reading.pagesNotNeeded} not needed
          </span>
        )}
        <span className="tag" title="Pages per call · calls at once">
          {reading.calls} call{reading.calls === 1 ? '' : 's'} · {reading.chunkSize} page{reading.chunkSize === 1 ? '' : 's'}/call · {reading.concurrency} at once
        </span>
        <span className="tag" title="Input / output tokens across every call">
          {reading.inputTokens.toLocaleString()} in · {reading.outputTokens.toLocaleString()} out
        </span>
        <span className="tag">${reading.costUsd.toFixed(4)}</span>
        <Badge variant="success" size="sm">{served.length} of {reading.joins.length} questions offered a block</Badge>
      </div>

      {missed.length > 0 && (
        <div style={{ fontSize: 13, marginBottom: 10 }}>
          <strong>Not offered: </strong>
          {[...missCounts.entries()].map(([reason, n]) => (
            <span key={reason} className="tag" style={{ marginRight: 6 }}
              title={missed.filter(j => j.missing === reason).map(j => `Q${j.questionIndex + 1}`).join(', ')}>
              {MISS_LABEL[reason] ?? reason} × {n}
            </span>
          ))}
        </div>
      )}

      {reading.pages.map(page => (
        <details key={page.pageIndex} style={{ marginBottom: 8 }} open={reading.pages.length <= 3}>
          <summary style={{ cursor: 'pointer', fontSize: 14 }}>
            <strong>Page {page.pageIndex + 1}</strong>{' '}
            {page.failed ? (
              <Badge variant="warning" size="sm" title={page.failed}>not read</Badge>
            ) : (
              <span className="muted">{page.blocks?.length ?? 0} block{page.blocks?.length === 1 ? '' : 's'}</span>
            )}{' '}
            <a href={page.pageUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>open page</a>
          </summary>
          {page.failed ? (
            <div className="muted" style={{ fontSize: 13, padding: '4px 0' }}>{page.failed}</div>
          ) : (
            (page.blocks ?? []).map((block, bi) => (
              <BlockRow key={bi} block={block} servesQuestions={servedBy.get(`${page.pageIndex}:${bi}`) ?? []} />
            ))
          )}
        </details>
      ))}
    </div>
  );
}
